import React from "react";

/**
 * Aviana Mandala — sacred lotus mandalas inspired by Holly's reference image.
 * Hand-crafted SVG with intricate lotus petals, dotwork, and luminous centers.
 *
 * Variants:
 *   "full"   — full intricate mandala (hero / pillars / dominant ornaments)
 *   "lotus"  — simplified outer petals (small icons)
 *   "seed"   — innermost seed-of-life (tiny accents)
 *   "sun"    — radial sun mandala with alternating rays (retreats)
 *   "star"   — 8-pointed star compass (trainings)
 *   "rose"   — rosette with twin petal rings (courses)
 *   "geo"    — sacred geometry hexagram lattice (events)
 */

function Petal({ size = 1, opacity = 1 }) {
  // A single ornate lotus petal pointing up from origin.
  return (
    <g opacity={opacity} transform={`scale(${size})`}>
      <path
        d="M0 -34 C -4 -28 -7 -22 -7 -16 C -7 -10 -4 -6 0 -4 C 4 -6 7 -10 7 -16 C 7 -22 4 -28 0 -34 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
      {/* Inner petal echo */}
      <path
        d="M0 -28 C -3 -23 -5 -19 -5 -15 C -5 -11 -2 -9 0 -8 C 2 -9 5 -11 5 -15 C 5 -19 3 -23 0 -28 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.8"
        opacity="0.7"
      />
      {/* Center seed dot */}
      <circle cx="0" cy="-20" r="0.9" fill="currentColor" />
      <circle cx="0" cy="-15" r="0.6" fill="currentColor" opacity="0.6" />
    </g>
  );
}

export default function Mandala({ size = 48, variant = "full", className = "", strokeWidth = 1, opacity = 1 }) {
  const stroke = "currentColor";

  if (variant === "seed") {
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" className={className} aria-hidden style={{ opacity }}>
        <g fill="none" stroke={stroke} strokeWidth={strokeWidth * 0.9}>
          <circle cx="32" cy="32" r="6" />
          {[0, 60, 120, 180, 240, 300].map((a) => (
            <circle key={a} cx="32" cy="32" r="6" transform={`rotate(${a} 32 32) translate(0 -6)`} />
          ))}
        </g>
      </svg>
    );
  }

  if (variant === "lotus") {
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" className={className} aria-hidden style={{ opacity }}>
        <g fill="none" stroke={stroke} strokeWidth={strokeWidth * 0.9} strokeLinejoin="round">
          {/* Outer lotus petals */}
          {Array.from({ length: 12 }, (_, i) => i * 30).map((a) => (
            <g key={a} transform={`rotate(${a} 32 32) translate(32 32)`}>
              <path d="M0 -28 C -3 -22 -5 -16 0 -10 C 5 -16 3 -22 0 -28 Z" />
            </g>
          ))}
          {/* Inner ring */}
          <circle cx="32" cy="32" r="9" />
          {/* Dotted middle ring */}
          {Array.from({ length: 16 }, (_, i) => i * 22.5).map((a) => (
            <circle key={a} cx="32" cy="14" r="0.9" fill={stroke} transform={`rotate(${a} 32 32)`} />
          ))}
          {/* Center seed */}
          <circle cx="32" cy="32" r="2.2" fill={stroke} />
        </g>
      </svg>
    );
  }

  if (variant === "sun") {
    // Radial sun mandala — alternating long/short rays, perfect for "Retreats"
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" className={className} aria-hidden style={{ opacity }}>
        <g fill="none" stroke={stroke} strokeWidth={strokeWidth * 0.9} strokeLinecap="round">
          {Array.from({ length: 24 }, (_, i) => i * 15).map((a) => (
            <g key={a} transform={`rotate(${a} 32 32)`}>
              {a % 30 === 0 ? (
                <line x1="32" y1="6" x2="32" y2="14" />
              ) : (
                <line x1="32" y1="10" x2="32" y2="14" />
              )}
            </g>
          ))}
          {Array.from({ length: 8 }, (_, i) => i * 45).map((a) => (
            <path key={`p-${a}`} d="M32 18 C29 22 28 26 32 30 C36 26 35 22 32 18 Z"
                  transform={`rotate(${a} 32 32)`} />
          ))}
          <circle cx="32" cy="32" r="9" />
          <circle cx="32" cy="32" r="3.5" />
          <circle cx="32" cy="32" r="1.1" fill={stroke} />
        </g>
      </svg>
    );
  }

  if (variant === "star") {
    // 8-pointed compass-star — perfect for "Trainings" (direction, initiation)
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" className={className} aria-hidden style={{ opacity }}>
        <g fill="none" stroke={stroke} strokeWidth={strokeWidth * 0.9} strokeLinejoin="round">
          {/* Outer ring */}
          <circle cx="32" cy="32" r="28" strokeWidth={strokeWidth * 0.6} />
          {/* Cardinal points (long rays) */}
          {[0, 90, 180, 270].map((a) => (
            <path key={`c-${a}`} d="M32 6 L36 30 L32 34 L28 30 Z" transform={`rotate(${a} 32 32)`} />
          ))}
          {/* Diagonal points (short rays) */}
          {[45, 135, 225, 315].map((a) => (
            <path key={`d-${a}`} d="M32 14 L34 30 L32 32 L30 30 Z"
                  transform={`rotate(${a} 32 32)`} opacity="0.85" />
          ))}
          {/* Inner ring + dot */}
          <circle cx="32" cy="32" r="6" />
          <circle cx="32" cy="32" r="2" fill={stroke} />
          {/* Tiny dots between rays */}
          {[22.5, 67.5, 112.5, 157.5, 202.5, 247.5, 292.5, 337.5].map((a) => (
            <circle key={`s-${a}`} cx="32" cy="10" r="0.9" fill={stroke}
                    transform={`rotate(${a} 32 32)`} />
          ))}
        </g>
      </svg>
    );
  }

  if (variant === "rose") {
    // Layered rosette — perfect for "Courses" (study, unfolding)
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" className={className} aria-hidden style={{ opacity }}>
        <g fill="none" stroke={stroke} strokeWidth={strokeWidth * 0.9} strokeLinejoin="round">
          {/* Outer petal ring (10) */}
          {Array.from({ length: 10 }, (_, i) => i * 36).map((a) => (
            <path key={`o-${a}`}
                  d="M32 4 C28 10 26 16 32 22 C38 16 36 10 32 4 Z"
                  transform={`rotate(${a} 32 32)`} />
          ))}
          {/* Inner offset petal ring (10) */}
          {Array.from({ length: 10 }, (_, i) => i * 36 + 18).map((a) => (
            <path key={`i-${a}`}
                  d="M32 14 C29 18 28 23 32 27 C36 23 35 18 32 14 Z"
                  transform={`rotate(${a} 32 32)`} opacity="0.8" />
          ))}
          {/* Center bud */}
          <circle cx="32" cy="32" r="4" />
          <circle cx="32" cy="32" r="1.4" fill={stroke} />
        </g>
      </svg>
    );
  }

  if (variant === "geo") {
    // Hexagram + lattice — perfect for "Events" (sacred geometry of gathering)
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" className={className} aria-hidden style={{ opacity }}>
        <g fill="none" stroke={stroke} strokeWidth={strokeWidth * 0.9} strokeLinejoin="round">
          {/* Outer hexagonal ring */}
          <polygon points="32,4 54,18 54,46 32,60 10,46 10,18" />
          {/* Upward + downward triangles forming hexagram */}
          <polygon points="32,10 50,42 14,42" />
          <polygon points="32,54 50,22 14,22" opacity="0.8" />
          {/* Inner hexagon */}
          <polygon points="32,18 44,25 44,39 32,46 20,39 20,25" />
          {/* Vertex dots */}
          {[
            [32, 4], [54, 18], [54, 46], [32, 60], [10, 46], [10, 18],
          ].map(([x, y]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r="1.2" fill={stroke} />
          ))}
          {/* Center seed */}
          <circle cx="32" cy="32" r="3" />
          <circle cx="32" cy="32" r="1" fill={stroke} />
        </g>
      </svg>
    );
  }

  // FULL — intricate sacred lotus mandala (matches Holly's reference)
  return (
    <svg width={size} height={size} viewBox="0 0 200 200" className={className} aria-hidden style={{ opacity }}>
      <g fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round">
        {/* Outermost dotted ring */}
        {Array.from({ length: 48 }, (_, i) => i * 7.5).map((a) => (
          <circle key={`d-${a}`} cx="100" cy="6" r="1.1" fill={stroke} transform={`rotate(${a} 100 100)`} />
        ))}

        {/* Outer petals (16) — large lotus crown */}
        {Array.from({ length: 16 }, (_, i) => i * 22.5).map((a) => (
          <g key={`op-${a}`} transform={`rotate(${a} 100 100) translate(100 100)`}>
            <path d="M0 -92 C -10 -78 -16 -64 -10 -52 C -4 -46 0 -44 0 -44 C 0 -44 4 -46 10 -52 C 16 -64 10 -78 0 -92 Z" />
            {/* Inner petal echo */}
            <path d="M0 -82 C -7 -72 -11 -62 -7 -54 C -3 -50 0 -48 0 -48 C 0 -48 3 -50 7 -54 C 11 -62 7 -72 0 -82 Z" opacity="0.7" />
            {/* Petal core dot */}
            <circle cx="0" cy="-62" r="2" fill={stroke} />
            <circle cx="0" cy="-56" r="1.1" fill={stroke} opacity="0.5" />
          </g>
        ))}

        {/* Ring between outer & middle petals (dotwork) */}
        <circle cx="100" cy="100" r="50" />
        {Array.from({ length: 36 }, (_, i) => i * 10).map((a) => (
          <circle key={`r1-${a}`} cx="100" cy="48" r="0.9" fill={stroke} transform={`rotate(${a} 100 100)`} />
        ))}

        {/* Middle petals (12) */}
        {Array.from({ length: 12 }, (_, i) => i * 30).map((a) => (
          <g key={`mp-${a}`} transform={`rotate(${a} 100 100) translate(100 100)`}>
            <path d="M0 -46 C -7 -38 -10 -28 -6 -22 C -2 -20 0 -18 0 -18 C 0 -18 2 -20 6 -22 C 10 -28 7 -38 0 -46 Z" />
            <path d="M0 -40 C -4 -34 -6 -28 -4 -24 C -1 -22 0 -22 0 -22 C 0 -22 1 -22 4 -24 C 6 -28 4 -34 0 -40 Z" opacity="0.7" />
            <circle cx="0" cy="-30" r="1.2" fill={stroke} opacity="0.7" />
          </g>
        ))}

        {/* Inner ring */}
        <circle cx="100" cy="100" r="22" />

        {/* Inner petals (8) — central rosette */}
        {Array.from({ length: 8 }, (_, i) => i * 45).map((a) => (
          <g key={`ip-${a}`} transform={`rotate(${a} 100 100) translate(100 100)`}>
            <path d="M0 -20 C -3 -16 -5 -12 -3 -8 C -1 -6 0 -6 0 -6 C 0 -6 1 -6 3 -8 C 5 -12 3 -16 0 -20 Z" />
            <circle cx="0" cy="-14" r="0.8" fill={stroke} />
          </g>
        ))}

        {/* Center seed flower */}
        <circle cx="100" cy="100" r="6" />
        <circle cx="100" cy="100" r="2.2" fill={stroke} />
      </g>
    </svg>
  );
}
